/**
 * Perguntas frequentes, formulário "Fale com um especialista", CTA final,
 * rodapé e botão flutuante de WhatsApp
 */

import { FormEvent, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronDown,
  Send,
  Loader2,
  CheckCircle2,
  ArrowRight,
  Mail,
  Phone,
  MessageCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { logoHorizontalDark, logoHorizontalLight } from '@assets/index';
import {
  sendSalesLead,
  getApiErrorMessage,
  PublicPlatformInfo,
  SubscriptionPlan,
} from '@services/platformService';
import { FAQ_ITEMS, SEGMENTS, BRAND } from '../content';
import { SEGMENT_PAGES } from '../segments';
import { Reveal, SectionHeading } from './Reveal';
import { openCookiePreferences, resolveMeasurementId } from '../analytics';
import { SiteAnalytics } from './SiteAnalytics';
import { privacyHref } from '../legal/company';

interface FaqProps {
  items?: { question: string; answer: string }[];
  title?: string;
}

export function Faq({ items = FAQ_ITEMS, title = 'Perguntas que sempre recebemos' }: FaqProps) {
  const [open, setOpen] = useState<number | null>(0);
  const baseId = useId();
  return (
    <section id="faq" className="relative scroll-mt-20 py-20 sm:py-28">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="Dúvidas frequentes" title={title} />
        <div className="mt-12 space-y-3">
          {items.map((item, i) => {
            const isOpen = open === i;
            const panelId = `${baseId}-faq-${i}`;
            return (
              <Reveal
                key={item.question}
                delay={i * 40}
                className="rounded-2xl border border-white/10 bg-white/[0.03]"
              >
                <h3>
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left font-semibold text-white"
                  >
                    {item.question}
                    <ChevronDown
                      size={20}
                      className={`shrink-0 text-violet-300 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                </h3>
                <div
                  id={panelId}
                  role="region"
                  hidden={!isOpen}
                  className="px-6 pb-6 leading-relaxed text-slate-400"
                >
                  {item.answer}
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const inputClass =
  'w-full rounded-xl border border-white/10 bg-slate-900/70 px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition focus:border-violet-400 focus:ring-2 focus:ring-violet-500/30';

/** Máscara simples de telefone brasileiro */
function maskPhone(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export function ContactSection({
  plans,
  selectedPlan,
  platform,
}: {
  plans: SubscriptionPlan[];
  selectedPlan: string;
  platform: PublicPlatformInfo | null;
}) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    segment: '',
    planSlug: selectedPlan,
    message: '',
    acceptContact: false,
    website: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (selectedPlan) setForm(f => ({ ...f, planSlug: selectedPlan }));
  }, [selectedPlan]);

  const set = (field: keyof typeof form, value: string | boolean) => {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => ({ ...e, [field]: '' }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (form.name.trim().length < 3) e.name = 'Informe seu nome';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = 'Email inválido';
    const digits = form.phone.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 11) e.phone = 'Telefone/WhatsApp inválido';
    if (!form.acceptContact) e.acceptContact = 'Autorize o contato para continuar';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    if (sending || !validate()) return;
    setSending(true);
    try {
      await sendSalesLead({
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        acceptContact: true,
      });
      setSent(true);
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, 'Não foi possível enviar agora. Tente novamente em instantes.'),
      );
    } finally {
      setSending(false);
    }
  };

  const whatsapp = platform?.supportWhatsApp?.replace(/\D/g, '');

  return (
    <section id="contato" className="relative scroll-mt-20 py-20 sm:py-28">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
        <div className="lg:col-span-2">
          <SectionHeading
            center={false}
            eyebrow="Fale com um especialista"
            title="Prefere conversar antes de começar?"
            description="Conte um pouco sobre o seu negócio. Nosso time mostra o sistema, ajuda a escolher o plano e tira todas as suas dúvidas."
          />
          <ul className="mt-8 space-y-4 text-sm text-slate-300">
            {platform?.supportEmail && (
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-violet-300" />
                <a
                  href={`mailto:${platform.supportEmail}`}
                  className="text-slate-300 hover:text-white"
                >
                  {platform.supportEmail}
                </a>
              </li>
            )}
            {platform?.supportPhone && (
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-violet-300" /> {platform.supportPhone}
              </li>
            )}
            {whatsapp && (
              <li className="flex items-center gap-3">
                <MessageCircle size={18} className="text-violet-300" />
                <a
                  href={`https://wa.me/${whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white"
                >
                  Chamar no WhatsApp
                </a>
              </li>
            )}
          </ul>
        </div>

        <Reveal className="lg:col-span-3">
          <div className="vw-gradient-border rounded-3xl bg-slate-950/80 p-6 sm:p-8">
            {sent ? (
              <div className="flex flex-col items-center py-10 text-center" role="status">
                <CheckCircle2 size={56} className="text-emerald-400" />
                <h3 className="mt-5 text-2xl font-bold text-white">Recebemos seu contato!</h3>
                <p className="mt-3 max-w-md text-slate-400">
                  Nosso time vai falar com você em breve. Enquanto isso, que tal já criar sua conta
                  e explorar o sistema?
                </p>
                <Link
                  to="/cadastro"
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 font-semibold text-slate-900 hover:bg-violet-100"
                >
                  Começar teste grátis <ArrowRight size={16} />
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="grid gap-4 sm:grid-cols-2">
                {/* Honeypot anti-spam: invisível para pessoas */}
                <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden>
                  <label>
                    Site
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website}
                      onChange={e => set('website', e.target.value)}
                    />
                  </label>
                </div>

                <Field label="Seu nome *" error={errors.name} htmlFor="lead-name">
                  <input
                    id="lead-name"
                    className={inputClass}
                    value={form.name}
                    onChange={e => set('name', e.target.value)}
                    autoComplete="name"
                    maxLength={100}
                  />
                </Field>
                <Field label="Nome do estabelecimento" htmlFor="lead-company">
                  <input
                    id="lead-company"
                    className={inputClass}
                    value={form.companyName}
                    onChange={e => set('companyName', e.target.value)}
                    autoComplete="organization"
                    maxLength={100}
                  />
                </Field>
                <Field label="Email *" error={errors.email} htmlFor="lead-email">
                  <input
                    id="lead-email"
                    type="email"
                    className={inputClass}
                    value={form.email}
                    onChange={e => set('email', e.target.value)}
                    autoComplete="email"
                    maxLength={150}
                  />
                </Field>
                <Field label="WhatsApp *" error={errors.phone} htmlFor="lead-phone">
                  <input
                    id="lead-phone"
                    type="tel"
                    inputMode="tel"
                    className={inputClass}
                    value={form.phone}
                    onChange={e => set('phone', maskPhone(e.target.value))}
                    placeholder="(11) 98765-4321"
                    autoComplete="tel"
                  />
                </Field>
                <Field label="Segmento" htmlFor="lead-segment">
                  <select
                    id="lead-segment"
                    className={inputClass}
                    value={form.segment}
                    onChange={e => set('segment', e.target.value)}
                  >
                    <option value="">Selecione</option>
                    {SEGMENTS.map(s => (
                      <option key={s.type} value={s.type}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Plano de interesse" htmlFor="lead-plan">
                  <select
                    id="lead-plan"
                    className={inputClass}
                    value={form.planSlug}
                    onChange={e => set('planSlug', e.target.value)}
                  >
                    <option value="">Ainda não sei</option>
                    {plans.map(p => (
                      <option key={p.slug} value={p.slug}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Mensagem" htmlFor="lead-message">
                    <textarea
                      id="lead-message"
                      rows={3}
                      className={inputClass}
                      value={form.message}
                      onChange={e => set('message', e.target.value)}
                      maxLength={1000}
                      placeholder="Ex.: tenho uma pizzaria com salão e delivery, recebo cerca de 40 pedidos por dia..."
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <label className="flex items-start gap-3 text-sm text-slate-400">
                    <input
                      type="checkbox"
                      checked={form.acceptContact}
                      onChange={e => set('acceptContact', e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-white/20 bg-slate-900 accent-violet-500"
                    />
                    <span>
                      Autorizo o {BRAND} a entrar em contato por email, telefone ou WhatsApp. Seus
                      dados são usados apenas para este atendimento, conforme a LGPD.
                    </span>
                  </label>
                  {errors.acceptContact && (
                    <p className="mt-1 text-xs text-rose-300">{errors.acceptContact}</p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <button
                    type="submit"
                    disabled={sending}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 py-3.5 font-semibold text-white shadow-lg shadow-violet-900/40 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                    {sending ? 'Enviando...' : 'Quero falar com um especialista'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Field({
  label,
  error,
  htmlFor,
  children,
}: {
  label: string;
  error?: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-slate-300">
        {label}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-rose-300">{error}</p>}
    </div>
  );
}

interface FinalCtaProps {
  trialDays: number;
  signupHref?: string;
  /** Âncora do formulário de contato (null esconde o botão) */
  contactHref?: string | null;
}

export function FinalCta({
  trialDays,
  signupHref = '/cadastro',
  contactHref = '#contato',
}: FinalCtaProps) {
  return (
    <section className="relative px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
      <Reveal className="relative mx-auto max-w-5xl overflow-hidden rounded-[2rem] bg-gradient-to-br from-violet-700 via-fuchsia-700 to-indigo-800 px-6 py-16 text-center shadow-2xl shadow-violet-950/60 sm:px-12">
        <div className="vw-grid absolute inset-0 opacity-40" aria-hidden />
        <div className="relative">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-5xl">
            Sua loja online pronta em minutos
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-violet-100">
            Crie sua conta agora e use o {BRAND} completo por {trialDays} dias. Sem cartão de
            crédito, sem fidelidade e sem comissão por pedido.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to={signupHref}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 font-semibold text-slate-900 shadow-xl transition hover:bg-violet-50 sm:w-auto"
            >
              Criar minha loja grátis
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
            {contactHref && (
              <a
                href={contactHref}
                className="inline-flex w-full items-center justify-center rounded-2xl border border-white/30 px-8 py-4 font-semibold text-white hover:bg-white/10 sm:w-auto"
              >
                Falar com um especialista
              </a>
            )}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

interface SiteFooterProps {
  platform: PublicPlatformInfo | null;
  /** Prefixo das âncoras: "" na home, "/" nas páginas de segmento */
  basePath?: string;
}

export function SiteFooter({ platform, basePath = '' }: SiteFooterProps) {
  const year = new Date().getFullYear();
  const privacyLink = privacyHref(platform?.privacyUrl);
  const measurementId = resolveMeasurementId(platform?.gaMeasurementId);
  return (
    <footer className="border-t border-white/10 bg-[#05070d]">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-5 lg:px-8">
        <div className="md:col-span-2">
          <img src={logoHorizontalDark} alt={BRAND} className="vw-logo-on-dark h-7 w-auto" />
          <img src={logoHorizontalLight} alt={BRAND} className="vw-logo-on-light h-7 w-auto" />
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
            Sistema completo de pedidos, cardápio digital e gestão para delivery e salão. Venda pelo
            seu próprio canal, sem comissão por pedido.
          </p>
        </div>
        <nav aria-label="Produto">
          <p className="text-sm font-semibold text-white">Produto</p>
          <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
            <li>
              <a href={`${basePath}#funcionalidades`} className="text-slate-400 hover:text-white">
                Funcionalidades
              </a>
            </li>
            <li>
              <a href={`${basePath}#planos`} className="text-slate-400 hover:text-white">
                Planos e preços
              </a>
            </li>
            <li>
              <a href={`${basePath}#implantacao`} className="text-slate-400 hover:text-white">
                Implantação
              </a>
            </li>
            <li>
              <a href={`${basePath}#faq`} className="text-slate-400 hover:text-white">
                Dúvidas frequentes
              </a>
            </li>
            <li>
              <Link to="/blog" className="text-slate-400 hover:text-white">
                Blog
              </Link>
            </li>
          </ul>
        </nav>
        <nav aria-label="Segmentos">
          <p className="text-sm font-semibold text-white">Para o seu negócio</p>
          <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
            {SEGMENT_PAGES.map(seg => (
              <li key={seg.slug}>
                <Link to={`/para/${seg.slug}`} className="text-slate-400 hover:text-white">
                  {seg.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
        <nav aria-label="Conta">
          <p className="text-sm font-semibold text-white">Conta</p>
          <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
            <li>
              <Link to="/cadastro" className="text-slate-400 hover:text-white">
                Criar conta grátis
              </Link>
            </li>
            <li>
              <Link to="/entrar" className="text-slate-400 hover:text-white">
                Entrar no painel
              </Link>
            </li>
            <li>
              <a href={`${basePath}#contato`} className="text-slate-400 hover:text-white">
                Fale conosco
              </a>
            </li>
            {platform?.termsUrl && (
              <li>
                <a
                  href={platform.termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-white"
                >
                  Termos de uso
                </a>
              </li>
            )}
            {privacyLink && (
              <li>
                <a
                  href={privacyLink}
                  {...(privacyLink.startsWith('/')
                    ? {}
                    : { target: '_blank', rel: 'noopener noreferrer' })}
                  className="text-slate-400 hover:text-white"
                >
                  Política de privacidade
                </a>
              </li>
            )}
            {measurementId && (
              <li>
                <button
                  type="button"
                  onClick={openCookiePreferences}
                  className="text-slate-400 hover:text-white"
                >
                  Preferências de cookies
                </button>
              </li>
            )}
          </ul>
        </nav>
      </div>
      <div className="border-t border-white/5 py-6 text-center text-xs text-slate-400">
        © {year} {BRAND}. Todos os direitos reservados.
      </div>
      <SiteAnalytics privacyUrl={privacyLink} measurementId={measurementId} />
      {/* Espaço para a barra fixa de CTA no celular não cobrir o rodapé */}
      <div className="h-20 lg:hidden" aria-hidden />
    </footer>
  );
}

export function WhatsAppFloat({ number }: { number?: string | null }) {
  const digits = number?.replace(/\D/g, '');
  if (!digits) return null;
  const text = encodeURIComponent(`Olá! Quero conhecer o ${BRAND}.`);
  return (
    <a
      href={`https://wa.me/${digits}?text=${text}`}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-24 right-4 z-40 flex h-14 w-14 lg:bottom-5 lg:right-5 items-center justify-center rounded-full bg-emerald-600 text-white shadow-xl shadow-emerald-900/40 transition hover:scale-105 hover:bg-emerald-700"
      aria-label="Falar pelo WhatsApp"
    >
      <MessageCircle size={26} />
    </a>
  );
}

/**
 * Barra fixa de conversão no celular: aparece depois que o visitante passa do
 * topo da página (o hero já tem o botão principal).
 */
export function MobileStickyCta({
  href = '/cadastro',
  label = 'Testar grátis por 30 dias',
}: {
  href?: string;
  label?: string;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 640);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-[#070a13]/90 px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 backdrop-blur-xl transition-transform duration-300 lg:hidden ${
        visible ? 'translate-y-0' : 'translate-y-full'
      }`}
      aria-hidden={!visible}
    >
      <Link
        to={href}
        tabIndex={visible ? 0 : -1}
        className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 py-3.5 font-semibold text-white shadow-lg shadow-violet-900/40"
      >
        {label}
        <ArrowRight size={18} />
      </Link>
    </div>
  );
}
